import React, { useEffect, useMemo, useState } from 'react';

import Seo from '../../components/seo';
import Layout from '../../components/layout';
import Hero from '../../components/hero';
import * as styles from './paint-plan.module.css';

function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

function normalizePercent(percent) {
  if (typeof percent !== 'number' || Number.isNaN(percent)) return 0;
  return percent <= 1 ? percent * 100 : percent;
}

function formatKey(key = '') {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatValue(value) {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (value === null || value === undefined || value === '') return 'N/A';

  return String(value);
}

const PaintPlan = (props) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const currentYear = useMemo(() => {
    const params = new URLSearchParams(props.location.search || '');
    const year = Number.parseInt(params.get('year'), 10);

    return Number.isNaN(year) ? new Date().getFullYear() : year;
  }, [props.location.search]);

  useEffect(() => {
    let isCancelled = false;

    async function getPaintPlan() {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`/api/paint-goals?year=${currentYear}`);
        if (!response.ok) {
          throw new Error('Failed to load paint plan');
        }

        const responseData = await response.json();

        if (!isCancelled) {
          setData(responseData);
        }
      } catch (fetchError) {
        if (!isCancelled) {
          setError(fetchError.message || 'Failed to load paint plan');
          setData(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    getPaintPlan();

    return () => {
      isCancelled = true;
    };
  }, [currentYear]);

  const percent = Math.round(clamp(normalizePercent(data?.percent), 0, 100));
  const detailEntries = Object.entries(data?.details || {}).filter(
    ([key]) =>
      !['Name', 'Goal', 'Painted', 'Percentage', 'Year', 'Miniatures'].includes(
        key
      )
  );
  const miniatureEntries = data?.miniatures || [];

  console.log({
    data,
  });

  return (
    <Layout location={props.location}>
      <Seo title={`Paint Plan ${currentYear}`} />
      <Hero
        title={data?.name || `Paint Plan ${currentYear}`}
        content={`Detailed painting plan and progress for ${currentYear}.`}
      />
      <section className={styles.container}>
        {loading && <p className={styles.status}>Loading paint plan...</p>}

        {!loading && error && (
          <p className={styles.error}>Unable to load plan: {error}</p>
        )}

        {!loading && !error && data && (
          <>
            <div className={styles.summaryGrid}>
              <div className={styles.statCard}>
                <h2>Progress</h2>
                <p className={styles.statValue}>{percent}%</p>
              </div>
              <div className={styles.statCard}>
                <h2>Painted</h2>
                <p className={styles.statValue}>{data.painted}</p>
              </div>
              <div className={styles.statCard}>
                <h2>Goal</h2>
                <p className={styles.statValue}>{data.goal}</p>
              </div>
              <div className={styles.statCard}>
                <h2>Year</h2>
                <p className={styles.statValue}>{data.year}</p>
              </div>
            </div>

            {data.notes && (
              <div className={styles.block}>
                <h2>Notes</h2>
                <p>{data.notes}</p>
              </div>
            )}

            <div className={styles.block}>
              <h2>Miniatures</h2>
              {miniatureEntries.length === 0 && (
                <p className={styles.status}>No related miniatures found.</p>
              )}

              {miniatureEntries.length > 0 && (
                <ul className={styles.miniatureList}>
                  {miniatureEntries.map((miniature) => (
                    <li key={miniature.id} className={styles.miniatureItem}>
                      <p className={styles.miniatureTitle}>{miniature.name}</p>
                      <div className={styles.miniatureMeta}>
                        {Object.entries(miniature.details || {})
                          .filter(([key]) =>
                            [
                              'Status',
                              'Faction',
                              'Unit',
                              'Quantity',
                              'No. Painted',
                            ].includes(key)
                          )
                          .map(([key, value]) => (
                            <span key={`${miniature.id}-${key}`}>
                              {formatKey(key)}: {formatValue(value)}
                            </span>
                          ))}
                      </div>
                      {miniature.url && (
                        <a
                          href={miniature.url}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open miniature in Notion
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className={styles.meta}>
              {data.lastEditedTime && (
                <p>
                  Last updated: {new Date(data.lastEditedTime).toLocaleString()}
                </p>
              )}
            </div>
          </>
        )}
      </section>
    </Layout>
  );
};

export default PaintPlan;
